
import React from 'react';
import { DataTable } from '../DataTable.tsx';
import * as columns from '../../misc/Columns';
import * as constants from '../../misc/Constants.tsx';
import { Button, LinearProgress } from '@mui/material';
import { useSnackbarMessage } from '../SnackMessge.tsx';
import { useDependencies } from '../../misc/DependenciesProvider.tsx';
import { VocabAddNew } from './VocabAddNew.tsx';
import { VocabEdit } from './VocabEdit.tsx';
import { sanitize_id } from '../../misc/CommonFunctions.ts';

export function DisplayTable({
    is_admin,
    tabName, db_name_of_vocab }: {
        is_admin: boolean,
        tabName: string, db_name_of_vocab: string
    }) {

    const [data, setData] = React.useState([]);
    const [columns, setColumns] = React.useState<columns.ColumnInfo[]>([]);
    const [showEdit, setShowEdit] = React.useState(false);
    const [editData, setEditData] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const { showMessage } = useSnackbarMessage();
    const { fracAPI } = useDependencies();
    const [showAddNew, setShowAddNew] = React.useState(false);

    React.useEffect(() => {
        loadData(tabName);

    }, [tabName, db_name_of_vocab]);

    const loadData = async (tabName) => {
        try {
            setIsLoading(true);
            const data = await fracAPI.getLookupData(db_name_of_vocab);
            setData(data);

            //get first row. note: this is a quick easy way to get this information. It wont work if there are no values in the table, but there should always be some values.
            if (data.length > 0) {
                var first_row = data[0];
                var keys = Object.keys(first_row);
                var cols: columns.ColumnInfo[] = keys.map((key) => {
                    return {
                        disp_width: '50px',
                        rows: 1,
                        wrap: true,
                        type: 'str',
                        max_disp_chars: 300,
                        hidden: false,
                        disp_name: createDisplayName(key),
                        editable: key == 'id' ? false : true,
                        id: key,
                    }
                });

                const edit_col: columns.ColumnInfo = {
                    rows: 1,
                    wrap: true,
                    type: 'action',
                    max_disp_width: 20,
                    hidden: false,
                    disp_name: 'Edit',
                    editable: false,
                    id: constants.editActionId,
                    action: 'Edit',
                    perform_action: async (x) => {
                        setShowEdit(true);
                        setEditData(x);
                    }
                }
                cols.push(edit_col);

                const del_col: columns.ColumnInfo = {
                    rows: 1,
                    wrap: true,
                    type: 'action',
                    max_disp_width: 20,
                    hidden: false,
                    disp_name: 'Delete',
                    editable: false,
                    id: constants.deleteActionId,
                    action: 'Delete',
                    perform_action: async (x) => {
                        // are you sure?
                        if (window.confirm("Are you sure you want to delete this record?")) {
                            handleDelete(String(x['id']));
                            loadData(tabName);
                        }
                    }
                }
                cols.push(del_col);

                setColumns(cols);
            }


        } catch (error) {
            console.error("Error loading data: " + error);
            showMessage("Error loading data. Please try again.");

        } finally {
            setIsLoading(false);
        }
    }

    const handleDelete = async (id) => {
        try {

            await fracAPI.deleteVocabularyRecord(db_name_of_vocab, id);

        } catch (error) {
            console.error("Error deleting record: " + error);
            showMessage("Error deleting record. Please try again.");
        }
    }



    return (
        <span>

            {isLoading && <>
                <LinearProgress />
            </>}

            {!isLoading && data && data.length > 0 &&
                <span id={"table" + sanitize_id(tabName)}>

                    <DataTable
                        showExport={is_admin}
                        getTokenFuncAsync={() => null}
                        keyname={'id'}
                        data={data}
                        columns={columns}
                        defaultSortColumn={"id"} >

                        <Button
                            id="add-new-button"
                            variant="outlined"
                            onClick={() => setShowAddNew(true)}

                        >
                            Add New
                        </Button>
                    </DataTable>

                    {showEdit &&
                        <VocabEdit
                            columns={columns}
                            db_name_of_vocab={db_name_of_vocab}
                            initialEditData={editData}
                            onHandleClose={() => {
                                setShowEdit(false);
                                loadData(tabName);
                            }}
                            tabName={tabName}
                        />
                    }

                    {showAddNew &&
                        <VocabAddNew
                            onHandleClose={() => {
                                setShowAddNew(false);
                                loadData(tabName);
                            }}
                            tabName={tabName}
                            db_name_of_vocab={db_name_of_vocab}
                            columns={columns} />
                    }
                </span>
            }


        </span >
    )
}

// from a name like 'scientiiic_name' return 'Scientific Name'
function createDisplayName(name: string): string {
    var parts = name.split('_');
    var display_name = parts.map((part) => {
        return part.charAt(0).toUpperCase() + part.slice(1);
    }).join(' ');

    return display_name;
}